import React, { RefObject, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./pagination.module.scss";

interface PaginationProps {
  pageSize: number;
  totalDataSize: number;
  onChange: (page: number) => void;
  currentPage: number,
  isPageLoading: boolean,
  paginationRef: RefObject<HTMLDivElement>
}

const Pagination: React.FC<PaginationProps> = ({
  pageSize,
  totalDataSize,
  onChange,
  currentPage,
  isPageLoading,
  paginationRef
}) => {
  const { t } = useTranslation();
  const [page, setPage] = useState<number>(currentPage);
  const totalPages = Math.ceil(totalDataSize / pageSize);

  useEffect(() => {
    setPage(currentPage)
  }, [currentPage])

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) {
      onChange(page);
      setPage(page);
    }
  };

  const renderPageNumbers = () => {
    let pages: (number | "...")[] = [];

    if (totalPages < 8) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    }

    if (totalPages === 8) {
      if (page < 3) {
        pages = [1, 2, 3, "...", 7, 8];
      } else if (page > 6) {
        pages = [1, 2, "...", 6, 7, 8];
      } else {
        pages = [1, 2, 3, 4, 5, 6, 7, 8];
      }
    }

    if (totalPages > 8) {
      if (page < 3) {
        pages.push(1, 2, 3, "...", totalPages);
      }

      if (page >= 3 && page <= 5) {
        if (page === 3) pages.push(1, 2, 3, 4, "...", totalPages);
        if (page === 4) pages.push(1, 2, 3, 4, 5, "...", totalPages);
        if (page === 5) pages.push(1, 2, 3, 4, 5, 6, "...", totalPages);
      }

      if (totalPages - page - 2 >= 3 && page - 3 >= 3) {
        pages.push(
          1,
          "...",
          page - 1,
          page,
          page + 1,
          "...",
          totalPages,
        );
      }

      if (totalPages - page < 2) {
        pages.push(1, "...", totalPages - 2, totalPages - 1, totalPages);
      }

      if (totalPages - page > 1 && totalPages - page < 5) {
        const t = totalPages;
        if (totalPages - page === 2)
          pages.push(1, "...", t - 3, t - 2, t - 1, t);
        if (totalPages - page === 3)
          pages.push(1, "...", t - 4, t - 3, t - 2, t - 1, t);
        if (totalPages - page === 4)
          pages.push(1, "...", t - 5, t - 4, t - 3, t - 2, t - 1, t);
      }
    }

    return pages.map((item, idx) => {
      if (typeof item === "number") {
        return (
          <button
            key={item}
            onClick={() => handlePageChange(item)}
            disabled={item === page || isPageLoading}
            style={{
              cursor: item === page ? "default" : "pointer",
              marginRight: idx === (pages.length - 1) ? "24px" : "16px"
            }}
            className={`${styles.paginationNumber} ${item === page && styles.paginationNumberCurrent}`}
          >
            {item}
          </button>
        );
      } else {
        return <div key={"dots" + idx} className={styles.paginationDots}>...</div>;
      }
    });
  };

  return (
    <div
        className={styles.pagination}
        ref={paginationRef}
    >
      <span className={styles.showingResults}>
        {t('pagination.showingResults', {
          start: (page - 1) * pageSize + 1,
          end: Math.min(page * pageSize, totalDataSize),
          total: totalDataSize
        })}
      </span>
      <div
        className={styles.paginationControls}
      >
        <button
          onClick={() => handlePageChange(page - 1)}
          disabled={page === 1 || isPageLoading}
          className={`${styles.paginationArrow} ${styles.paginationLeftArrow}`}
        >
        </button>
        {renderPageNumbers()}
        <button
          onClick={() => handlePageChange(page + 1)}
          disabled={page === totalPages || isPageLoading}
          className={`${styles.paginationArrow} ${styles.paginationRightArrow}`}
        >
        </button>
      </div>
    </div>
  );
};

export default Pagination;
